import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState, useCallback } from 'react';

import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';

import { MarsinetContext } from '../../../../../context/marsinetProvider';
import { Alarm, DeviceDto, Session } from '../../../../../typescript-axios';
import {
  SessionData,
  getTotalsOfSessions,
  getSessionsFilteredByDate,
} from '@marsinet/commons';

import SessionSummaryBar from '../../../patients/patient/components/session-summary-bar/session-summary-bar';
import EventList from '../../../sessions/components/event-list/event-list';
import CustomCalendar from '../../../../../../../src/app/components/calendar/calendar';
import {
  configureAxios,
  getDeviceModelLabel,
} from '../../../../../../../src/app/utils/utils';
import { apiServices } from '../../../../../../../src/app/services/apiServices';
import { useToast } from '../../../../../../../src/app/context/ToastProvider';
import { defaultSessionData } from '../../../../../../../src/app/constants/constants';
import { analytics } from '../../../../../../../src/firebase';
import { logEvent } from 'firebase/analytics';
import {
  getCalendarDateInTimeZone,
  getUtcDateKeyInTimeZone,
  getUtcDayRange,
} from '../../../../../../../src/app/utils/timezone-utils';

export function Alarms({
  preselectedDeviceId,
}: {
  preselectedDeviceId?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const { access_token, setRole, setAdmin, setError, selectedTimezone } =
    useContext(MarsinetContext);
  const { showToast: show } = useToast();

  const [devices, setDevices] = useState<DeviceDto[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<DeviceDto | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [allDeviceAlarms, setAllDeviceAlarms] = useState<Alarm[]>([]);
  const [highlightedDates, setHighlightedDates] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date>(() =>
    getCalendarDateInTimeZone(new Date(), selectedTimezone)
  );
  const [updateAlarms, setUpdateAlarms] = useState<boolean>(false);
  const [alarms, setAlarms] = useState<Alarm[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingDate, setLoadingDate] = useState<boolean>(false);
  const [totalSession, setTotalSession] =
    useState<SessionData>(defaultSessionData);
  const [selectedAlarms, setSelectedAlarms] = useState<Alarm[]>([]); // persistent alarms for search

  // Fetch devices on initial load
  useEffect(() => {
    const fetchDevices = async () => {
      if (!access_token) return;

      configureAxios(access_token);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any =
          await apiServices.myAccountApi.myAccountControllerGetMyDevices();

        const sortedDevices = response.data['devices']
          .map((d: DeviceDto) => ({
            ...d,
            modelLabel: getDeviceModelLabel(d.model),
          }))
          .sort((a, b) => (a.serial || '').localeCompare(b.serial || ''));

        setDevices(sortedDevices);

        const targetDevice =
          (preselectedDeviceId &&
            sortedDevices.find((d) => d.d_id === preselectedDeviceId)) ||
          sortedDevices[0];
        setSelectedDevice(targetDevice);
      } catch (error) {
        if (error.response?.status !== 401) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
          });
        }
      }
    };

    fetchDevices();
  }, [access_token, setError, preselectedDeviceId]);

  //actualizar alarmas seleccionadas al cambiar de fecha
  useEffect(() => {
    setSelectedAlarms([]); // Borra las seleccionadas al cambiar de fecha
  }, [startDate]);

  // Fetch alarms for a specific date
  const fetchAlarmsByDate = useCallback(async () => {
    if (!access_token || !selectedDevice) return;

    setLoadingDate(true);
    configureAxios(access_token);
    try {
      // Envía el día seleccionado como límites UTC exactos; la API no depende de la zona.
      const { start, endExclusive } = getUtcDayRange(
        startDate,
        selectedTimezone
      );
      const alarmsResponse =
        await apiServices.alarmApi.alarmControllerFindAllAlarmOfDevicePerDay(
          selectedDevice.d_id,
          start.toISOString(),
          {
            params: {
              endDate: endExclusive.toISOString(),
            },
          }
        );

      setAlarms(
        alarmsResponse.data.sort(
          (a: Alarm, b: Alarm) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        )
      );
    } catch (error) {
      if (error.response?.status !== 401) {
        setError(true);
        logEvent(analytics, 'error_marsinet', {
          error_message: error.message,
          error_code: error.response?.status,
          error_page: window.location.pathname,
          error_stack: error.stack || 'No stack trace',
          timestamp: new Date().toISOString(),
          browser_info: navigator.userAgent,
        });
      }
    }
    setLoadingDate(false);
  }, [access_token, selectedDevice, startDate, selectedTimezone, setError]);

  useEffect(() => {
    if (selectedDevice) {
      fetchAlarmsByDate();
    }
  }, [fetchAlarmsByDate, startDate, selectedDevice]);

  useEffect(() => {
    setHighlightedDates(
      allDeviceAlarms
        // Resalta los días de alarma según la zona seleccionada, no según la medianoche UTC.
        .map((alarm) =>
          getUtcDateKeyInTimeZone(alarm.timestamp, selectedTimezone)
        )
        .sort()
    );
  }, [allDeviceAlarms, selectedTimezone]);

  const handleChangeSelectedDevice = useCallback((e: { value: DeviceDto }) => {
    setSelectedDevice(e.value);
    setHighlightedDates([]);
  }, []);

  const selectedDeviceTemplate = useCallback(
    (option: DeviceDto) =>
      option ? (
        <div>
          <span>{option.serial}</span>
          <span className="hidden md:inline">
            {' - '}
            {getDeviceModelLabel(option.model)}
          </span>
        </div>
      ) : (
        <div>{t('selectDevice')}</div>
      ),
    [t]
  );

  const devicesTemplateDropdown = useCallback(
    (option: DeviceDto) => (
      <div>
        <span>{option.serial}</span>
        <span className="hidden md:inline">
          {' - '}
          {getDeviceModelLabel(option.model)}
        </span>
      </div>
    ),
    []
  );

  // Fetch sessions and all alarms when the selected device changes.
  const fetchSessionsAndAlarms = useCallback(async () => {
    if (!access_token || !selectedDevice) return;

    setLoading(true);
    configureAxios(access_token);
    try {
      const [sessionsResponse, dataResponse, alarmsAllResponse] =
        await Promise.all([
          apiServices.sessionApi.sessionControllerFindAllSessionsOfDevice(
            selectedDevice.d_id
          ),
          apiServices.myAccountApi.myAccountControllerGetData(),
          apiServices.alarmApi.alarmControllerFindAllAlarmOfDevice(
            selectedDevice.d_id
          ),
        ]);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sessionData: any = sessionsResponse.data;
      const sessions = getSessionsFilteredByDate(sessionData);
      setSessions(sessions);
      setTotalSession(getTotalsOfSessions(sessions));

      setAllDeviceAlarms(alarmsAllResponse.data);

      const data = dataResponse.data;
      setRole(data.organization?.role);
      setAdmin(data.can_edit);
    } catch (_error) {
      show('info', t('messages.info'), t('emptyAlarms'));
    }
    setLoading(false);
    setUpdateAlarms(false);
  }, [access_token, selectedDevice, setRole, setAdmin, show, t]);

  useEffect(() => {
    if (selectedDevice) {
      fetchSessionsAndAlarms();
    }
  }, [selectedDevice, i18n.language, updateAlarms, fetchSessionsAndAlarms]);

  return (
    <div className="col-12 grid grid-nogutter">
      <div className="surface-card border-round-2xl col-12 p-4">
        <div className="col-12 px-0 grid grid-nogutter text-center md:text-left">
          <div className="col-6 mb-3 xl:mb-0 flex align-items-center flex-column xl:flex-row gap-2">
            <div className="p-fluid w-full text-center">
              <label className="font-bold block mb-2">
                {t('selectDevice')}:
              </label>
              <Dropdown
                value={selectedDevice}
                onChange={handleChangeSelectedDevice}
                options={devices}
                optionLabel="serial"
                placeholder={t('selectDevice')}
                valueTemplate={selectedDeviceTemplate}
                itemTemplate={devicesTemplateDropdown}
                showClear
                filter
                filterBy="serial,modelLabel"
              />
            </div>
          </div>
          <div className="col-6 mb-3 xl:mb-0 flex align-items-center flex-column xl:flex-row gap-2">
            <div className="p-fluid w-full text-center">
              <CustomCalendar
                highlightedDates={highlightedDates}
                startDate={startDate}
                setStartDate={setStartDate}
              />
            </div>
          </div>
        </div>
        {!selectedDevice && (
          <div className="col-12">
            <div className="text-center text-700 font-bold py-6">
              {t('selectOneDevice')}
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex justify-content-center align-items-center h-full">
            <ProgressSpinner />
          </div>
        ) : (
          selectedDevice && (
            <>
              <div className="col-12 px-0">
                <SessionSummaryBar
                  total_sessions={sessions.length}
                  total_steps={totalSession.total_steps_total}
                  total_time={totalSession.total_time_use}
                  total_time_walking={totalSession.total_time_walking}
                  total_time_standing={totalSession.total_time_standing}
                  average_steps_session={totalSession.median_total_steps}
                  average_time_session={totalSession.median_total_time}
                />
              </div>
              <div className="col-12 px-0 grid grid-nogutter text-center md:text-left">
                <div className="grid grid-nogutter h-full w-full">
                  <div className="col-12">
                    <div className="surface-100 h-full container-graphs__shadow-customs-graphs md:p-4 p-2 border-round-2xl text-center">
                      <div className="grid h-full">
                        <div className="col-12">
                          {loadingDate ? (
                            <div className="flex justify-content-center align-items-center h-full">
                              <ProgressSpinner />
                            </div>
                          ) : highlightedDates.length === 0 ? (
                            <div className="text-center text-700 font-bold py-6">
                              {t('emptyAlarms')}
                            </div>
                          ) : (
                            <EventList
                              alarms={alarms}
                              setUpdateAlarms={setUpdateAlarms}
                              loading={loading}
                              model={selectedDevice?.model}
                              selectedAlarms={selectedAlarms}
                              setSelectedAlarms={setSelectedAlarms}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}

export default Alarms;
